import { v4 as uuidv4 } from 'uuid';

import {
  DEFAULT_METADATA_KEYS,
  LOCKED_METADATA_KEYS,
  RESERVED_METADATA_KEYS,
} from '../constants';
import type {
  KeyValueEntry,
  MetadataEditFormState,
  MetadataObject,
  StringEntry,
} from '../types';

const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

const sourceToStringEntries = (source: unknown): StringEntry[] => {
  if (Array.isArray(source)) {
    return source
      .map(item => stringifyValue(item))
      .map(value => ({ id: uuidv4(), value }));
  }
  if (source && typeof source === 'object') {
    return Object.keys(source as MetadataObject).map(key => ({
      id: uuidv4(),
      value: key,
    }));
  }
  return [];
};

export const createEmptyEntry = (): KeyValueEntry => ({
  id: uuidv4(),
  key: '',
  value: '',
});

export const createEmptyStringEntry = (): StringEntry => ({
  id: uuidv4(),
  value: '',
});

export const parseMetadataInput = (
  input?: string | MetadataObject | null
): MetadataObject => {
  if (!input) return {};
  if (typeof input === 'object') return input as MetadataObject;
  try {
    const parsed = JSON.parse(input);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as MetadataObject)
      : {};
  } catch {
    return {};
  }
};

export const buildInitialFormState = (
  metadata?: string | MetadataObject | null
): MetadataEditFormState => {
  const source = parseMetadataInput(metadata);

  return {
    company_name: stringifyValue(source[DEFAULT_METADATA_KEYS.COMPANY_NAME]),
    company_overview: stringifyValue(
      source[DEFAULT_METADATA_KEYS.COMPANY_OVERVIEW]
    ),
    stt_keywords: sourceToStringEntries(
      source[DEFAULT_METADATA_KEYS.STT_KEYWORDS]
    ),
    custom_fields: Object.entries(source)
      .filter(([key]) => !RESERVED_METADATA_KEYS.has(key))
      .map(([key, value]) => ({
        id: uuidv4(),
        key,
        value: stringifyValue(value),
      })),
  };
};

const stringEntriesToArray = (entries: StringEntry[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  entries.forEach(({ value }) => {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    result.push(trimmed);
  });
  return result;
};

export const buildMetadataPayload = (
  state: MetadataEditFormState
): MetadataObject => {
  const payload: MetadataObject = {};

  if (state.company_name.trim()) {
    payload[DEFAULT_METADATA_KEYS.COMPANY_NAME] = state.company_name;
  }
  if (state.company_overview.trim()) {
    payload[DEFAULT_METADATA_KEYS.COMPANY_OVERVIEW] = state.company_overview;
  }

  const sttArray = stringEntriesToArray(state.stt_keywords);
  if (sttArray.length) {
    payload[DEFAULT_METADATA_KEYS.STT_KEYWORDS] = sttArray;
  }

  state.custom_fields.forEach(({ key, value }) => {
    const trimmedKey = key.trim();
    if (!trimmedKey) return;
    if (RESERVED_METADATA_KEYS.has(trimmedKey)) return;
    if (!value.trim() && !LOCKED_METADATA_KEYS.has(trimmedKey)) return;
    payload[trimmedKey] = value;
  });

  return payload;
};

const hasInvalidKeyValueEntries = (entries: KeyValueEntry[]): boolean =>
  entries.some(({ key, value }) => {
    const trimmedKey = key.trim();
    if (!trimmedKey) return true;
    if (LOCKED_METADATA_KEYS.has(trimmedKey)) return false;
    return !value.trim();
  });

const hasDuplicateKeys = (entries: KeyValueEntry[]): boolean => {
  const seen = new Set<string>();
  for (const { key } of entries) {
    const trimmed = key.trim();
    if (!trimmed) continue;
    if (seen.has(trimmed)) return true;
    seen.add(trimmed);
  }
  return false;
};

const hasInvalidStringEntries = (entries: StringEntry[]): boolean =>
  entries.some(({ value }) => !value.trim());

const hasDuplicateStringValues = (entries: StringEntry[]): boolean => {
  const seen = new Set<string>();
  for (const { value } of entries) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (seen.has(trimmed)) return true;
    seen.add(trimmed);
  }
  return false;
};

export const isFormValid = (state: MetadataEditFormState): boolean => {
  if (hasInvalidStringEntries(state.stt_keywords)) return false;
  if (hasDuplicateStringValues(state.stt_keywords)) return false;
  if (hasInvalidKeyValueEntries(state.custom_fields)) return false;
  if (hasDuplicateKeys(state.custom_fields)) return false;
  return true;
};

export const stringifyMetadataForApi = (
  state: MetadataEditFormState
): string => {
  const payload = buildMetadataPayload(state);
  return Object.keys(payload).length ? JSON.stringify(payload) : '';
};

export const stringifyMetadataPreview = (
  state: MetadataEditFormState
): string => JSON.stringify(buildMetadataPayload(state), null, 2);
